import { Box, Button } from "@mui/material";
import React, { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import PostComment from "./PostComment";
import { formatTimeAgo } from "@/helpers/getHoursDifference";
import theme from "@/theme";
import { useLazyGetRepliesPostCommentsQuery } from "@/store/api/postCommentApi";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";

interface CommentThreadProps {
  comment: any;
  anchorEls: Record<string, HTMLElement | null>;
  handleClick: (uid: string) => (event: React.MouseEvent<HTMLElement>) => void;
  handleClose: (uid: string) => () => void;
  handleDelete: (uid: string) => void;
  handleReport: (uid: string) => void;
  setOpenEditModal: Dispatch<
    SetStateAction<{
      open: boolean;
      uid: string;
      comment: string;
      avatar: string;
    }>
  >;
  setReplyUid: (uid: string) => void;
  setOpenReplyModal: Dispatch<
    SetStateAction<{
      open: boolean;
      comment: string;
      uid: string;
      handle: string;
      username: string;
      avatar: string;
      time: string;
      level: number;
    }>
  >;
  setEdit: Dispatch<SetStateAction<any>>;
  level: number;
  openReplies: Record<string, boolean>;
  loadedReplies: Record<string, any[]>;
  toggleReplies: (uid: string) => void;
  setLoadedReplies: Dispatch<SetStateAction<Record<string, any[]>>>;
  status?: "pending" | "error" | "published";
  isLast?: boolean;
}
const LIMIT = 15;
const CommentThread: FC<CommentThreadProps> = ({
  comment,
  anchorEls,
  handleClick,
  handleClose,
  handleDelete,
  handleReport,
  setOpenEditModal,
  setOpenReplyModal,
  setReplyUid,
  setEdit,
  level,
  openReplies,
  loadedReplies,
  toggleReplies,
  setLoadedReplies,
  status,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [getReplies] = useLazyGetRepliesPostCommentsQuery();
  const inlineReplies = comment.replies?.data || [];
  const repliesToShow = loadedReplies[comment.uid] || inlineReplies;
  const totalReplies =
    comment.repliesCount || loadedReplies[comment.uid]?.length || inlineReplies.length || 0;
  const hasReplies = totalReplies > 0;
  const isOpen = openReplies[comment.uid] || false;
  const shouldShowReplies = isOpen && repliesToShow.length > 0;
  const [page, setPage] = useState<Record<string, number>>({});

  const handleToggleReplies = async () => {
    if (!isOpen && hasReplies) {
      setIsLoading(true);
      try {
        const res = await getReplies({ repliesUid: comment.uid, page: 1, limit: LIMIT }).unwrap();
        const fetched = res.data || res || [];
        const parentComment = res.meta?.parentComment;

        const enrichedReplies = fetched.map((reply: any) => ({
          ...reply,
          avatar: reply.author?.image?.url || "",
          parentComment: parentComment
            ? {
                avatar: parentComment.author?.image?.url || "",
                username: parentComment.author?.username || "",
                comment: parentComment.content || "",
                createdAt: parentComment.createdAt || "",
              }
            : undefined,
        }));

        setLoadedReplies((prev) => ({
          ...prev,
          [comment.uid]: enrichedReplies,
        }));

        setPage((prev) => ({ ...prev, [comment.uid]: 1 }));
      } catch (error) {
        console.error("Failed to fetch replies:", error);
      } finally {
        setIsLoading(false);
      }
    }
    toggleReplies(comment.uid);
  };

  const handleLoadMoreReplies = async () => {
    if (isLoading) return;
    const nextPage = (page[comment.uid] || 1) + 1;
    setIsLoading(true);
    try {
      const res = await getReplies({
        repliesUid: comment.uid,
        page: nextPage,
        limit: LIMIT,
      }).unwrap();
      const fetched = res.data || res || [];
      const parentComment = res.meta?.parentComment;

      const enrichedReplies = fetched.map((reply: any) => ({
        ...reply,
        avatar: reply.author?.image?.url || "",
        parentComment: parentComment
          ? {
              avatar: parentComment.author?.image?.url || "",
              username: parentComment.author?.username || "",
              comment: parentComment.content || "",
              createdAt: parentComment.createdAt || "",
            }
          : undefined,
      }));

      setLoadedReplies((prev) => {
        const existing = prev[comment.uid] || [];
        const seen = new Set(existing.map((r: any) => r.uid));
        const newReplies = enrichedReplies.filter((r: any) => !seen.has(r.uid));
        return {
          ...prev,
          [comment.uid]: [...existing, ...newReplies],
        };
      });

      setPage((prev) => ({ ...prev, [comment.uid]: nextPage }));
    } catch (error) {
      console.error("Failed to load more replies:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (comment.replies?.data?.length > 0 && !loadedReplies[comment.uid]) {
      setLoadedReplies((prev) => ({
        ...prev,
        [comment.uid]: [...(prev[comment.uid] || []), ...comment.replies.data],
      }));
    }
  }, [comment, loadedReplies, setLoadedReplies]);

  return (
    <Box
      data-comment-id={comment.uid}
      sx={{
        ml: level > 0 ? 4 : 0,
        mt: level === 0 ? "12px" : "30px",
        position: "relative",
        borderBottom: level === 0 && hasReplies && isOpen ? "2px solid #F4F5F6" : "none",
        paddingBottom: level === 0 ? "18px" : 0,
      }}
    >
      <PostComment
        setOpenReplyModal={setOpenReplyModal}
        setReplyUid={setReplyUid}
        setEdit={setEdit}
        uid={comment.uid}
        username={comment.author.name}
        authorId={comment.author.uid}
        handle={comment.author.username}
        changed={false}
        zaps={0}
        time={formatTimeAgo(comment.updatedAt)}
        likePost={comment.reactionsCount}
        comment={comment.content}
        anchorEl={anchorEls[comment.uid]}
        open={Boolean(anchorEls[comment.uid])}
        handleClick={handleClick(comment.uid)}
        handleClose={handleClose(comment.uid)}
        handleDelete={handleDelete}
        handleReport={handleReport}
        setOpenEditModal={setOpenEditModal}
        isLiked={comment.isLiked}
        status={status}
        commentData={comment}
        handleToggleReplies={handleToggleReplies}
        totalReplies={totalReplies}
        isOpenReplies={isOpen}
        isLoading={isLoading}
        level={level}
      />

      {hasReplies && (
        <Box sx={{ position: "relative" }}>
          {shouldShowReplies && (
            <Box sx={{ mt: 1 }}>
              {repliesToShow.map((reply) => (
                <CommentThread
                  key={reply.uid}
                  comment={reply}
                  anchorEls={anchorEls}
                  handleClick={handleClick}
                  handleClose={handleClose}
                  handleDelete={handleDelete}
                  handleReport={handleReport}
                  setOpenEditModal={setOpenEditModal}
                  setOpenReplyModal={setOpenReplyModal}
                  setReplyUid={setReplyUid}
                  setEdit={setEdit}
                  level={level + 1}
                  openReplies={openReplies}
                  loadedReplies={loadedReplies}
                  toggleReplies={toggleReplies}
                  setLoadedReplies={setLoadedReplies}
                  status={reply.pending ? "pending" : reply.error ? "error" : "published"}
                />
              ))}

              {repliesToShow.length < totalReplies && (
                <Button
                  sx={{
                    textTransform: "none",
                    color: isLoading
                      ? theme.palette.custom.mainBlueDisable
                      : theme.palette.custom.mainBlue,
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    mt: 1,
                    marginLeft: "80px",
                    gap: "6px",
                  }}
                  onClick={handleLoadMoreReplies}
                >
                  Show more replies
                  {isLoading && <CustomLoader size={18} />}
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CommentThread;
